import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export type FileOperationProgress =
  | { phase: 'preparing' | 'downloading' | 'loading' | 'uploading' }
  | {
      phase: 'truncating';
      pagesProcessed: number;
      totalPages: number;
    };

export type FileOperationState =
  | { type: 'simple'; message: string }
  | {
      type: 'progress';
      message: string;
      fileName?: string;
      progress: FileOperationProgress;
    };

type FileOperationOverlayContextValue = {
  operation: FileOperationState | null;
  showSimpleOverlay: (message: string) => void;
  showProgressOverlay: (message: string, progress: FileOperationProgress, fileName?: string) => void;
  updateProgress: (progress: FileOperationProgress) => void;
  hideOverlay: () => void;
};

const FileOperationOverlayContext = createContext<FileOperationOverlayContextValue | undefined>(undefined);

export function FileOperationOverlayProvider({ children }: { children: ReactNode }) {
  const [operation, setOperation] = useState<FileOperationState | null>(null);

  const showSimpleOverlay = useCallback((message: string) => {
    setOperation({ type: 'simple', message });
  }, []);

  const showProgressOverlay = useCallback((message: string, progress: FileOperationProgress, fileName?: string) => {
    setOperation({ type: 'progress', message, progress, fileName });
  }, []);

  const updateProgress = useCallback((progress: FileOperationProgress) => {
    setOperation((prev) => {
      if (prev?.type === 'progress') {
        return { ...prev, progress };
      }
      return prev;
    });
  }, []);

  const hideOverlay = useCallback(() => {
    setOperation(null);
  }, []);

  const value = useMemo<FileOperationOverlayContextValue>(
    () => ({
      operation,
      showSimpleOverlay,
      showProgressOverlay,
      updateProgress,
      hideOverlay,
    }),
    [operation, showSimpleOverlay, showProgressOverlay, updateProgress, hideOverlay],
  );

  return <FileOperationOverlayContext.Provider value={value}>{children}</FileOperationOverlayContext.Provider>;
}

export function useFileOperationOverlay(): FileOperationOverlayContextValue {
  const context = useContext(FileOperationOverlayContext);
  if (!context) {
    throw new Error('useFileOperationOverlay must be used within <FileOperationOverlayProvider>.');
  }
  return context;
}
